'use client';

import { use, useEffect, useState } from 'react';
import BookList from '@/components/reader/BookList';
import ChapterView from '@/components/reader/ChapterView';
import { getChapter } from '@/lib/api';
import type { VerseReference } from '@/lib/types';
import Link from 'next/link';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { BIBLE_BOOKS } from '@/lib/constants';

export default function ChapterPage({
  params,
}: {
  params: Promise<{ book: string; chapter: string }>;
}) {
  const { book: bookParam, chapter: chapterParam } = use(params);
  const bookName = decodeURIComponent(bookParam);
  const chapterNum = parseInt(chapterParam, 10);
  const [verses, setVerses] = useState<VerseReference[]>([]);
  const [loading, setLoading] = useState(true);

  const bookData = BIBLE_BOOKS.find((b) => b.name === bookName);
  const maxChapter = bookData?.chapters || 0;

  useEffect(() => {
    setLoading(true);
    getChapter(bookName, chapterNum)
      .then((data) => setVerses(data.verses))
      .catch(() => setVerses([]))
      .finally(() => setLoading(false));
  }, [bookName, chapterNum]);

  return (
    <div className="flex h-screen">
      {/* Book sidebar */}
      <div
        className="w-64 shrink-0 overflow-y-auto p-4"
        style={{
          backgroundColor: 'var(--bg-secondary)',
          borderRight: '1px solid rgba(200, 164, 94, 0.1)',
        }}
      >
        <BookList activeBook={bookName} />
      </div>

      {/* Chapter content */}
      <div className="flex-1 overflow-y-auto p-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 mb-6 text-xs" style={{ color: 'var(--text-secondary)' }}>
          <Link href="/reader" className="hover:underline">Reader</Link>
          <span>/</span>
          <Link href={`/reader/${encodeURIComponent(bookName)}`} className="hover:underline">{bookName}</Link>
          <span>/</span>
          <span style={{ color: 'var(--accent-gold)' }}>Chapter {chapterNum}</span>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div
              className="w-6 h-6 border-2 rounded-full animate-spin"
              style={{
                borderColor: 'rgba(200, 164, 94, 0.2)',
                borderTopColor: 'var(--accent-gold)',
              }}
            />
          </div>
        ) : (
          <ChapterView book={bookName} chapter={chapterNum} verses={verses} />
        )}

        {/* Chapter navigation */}
        <div className="flex items-center justify-between mt-8 pt-6" style={{ borderTop: '1px solid rgba(200, 164, 94, 0.1)' }}>
          {chapterNum > 1 ? (
            <Link
              href={`/reader/${encodeURIComponent(bookName)}/${chapterNum - 1}`}
              className="flex items-center gap-1 text-sm px-4 py-2 rounded-lg transition-colors hover:bg-white/5"
              style={{ color: 'var(--text-secondary)' }}
            >
              <ChevronLeft size={16} />
              Chapter {chapterNum - 1}
            </Link>
          ) : (
            <div />
          )}
          {chapterNum < maxChapter ? (
            <Link
              href={`/reader/${encodeURIComponent(bookName)}/${chapterNum + 1}`}
              className="flex items-center gap-1 text-sm px-4 py-2 rounded-lg transition-colors hover:bg-white/5"
              style={{ color: 'var(--text-secondary)' }}
            >
              Chapter {chapterNum + 1}
              <ChevronRight size={16} />
            </Link>
          ) : (
            <div />
          )}
        </div>
      </div>
    </div>
  );
}
